import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import styled from 'styled-components';
import isEmail from 'validator/es/lib/isEmail';
import * as Yup from 'yup';
import { AuthMarketing, Button, ButtonSignUp, FieldWrapper, FormError, Input, LayoutSplit, Password, Text } from 'components';
import { AuthButtonGroup, AuthContainer, AuthContent, AuthContentInner, AuthContentLayout, AuthFieldset, AuthOrContainer } from 'components/Auth/styles';
import Form from 'components/Form/Form';
import { theme, toRem } from 'design';
import useConnectIntegration from 'hooks/useConnectIntegration';
import { DeviceService } from 'lib';
import AuthLogoutNotice from 'screens/Auth/AuthLogoutNotice';
import { clearAllRequests, selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { loginRequest } from 'store/App';
import * as ActionTypes from 'store/App/types';
import { IStore } from 'store/types';
import { AuthType, IApiErrorPayloadWithCode, IAuthMFARequired, IAuthRequest } from 'types';

interface IMFARequired {
  required: boolean;
  mfa?: IAuthMFARequired;
}

interface ILoginProps {
  handleGoogleLogin: () => void;
  authHeader: React.ReactNode;
  authTerms: React.ReactNode;
  authMarketing?: React.ReactNode;
  renderMFAVerification: ({ mfaRequired, onBack }: { mfaRequired: IAuthMFARequired; onBack: () => void }) => React.ReactNode;
  onForgotPasswordClick: () => void;
  signupText?: string;
}

const AltAuthContentInner = styled(AuthContentInner)`
  max-width: ${toRem(480)};
`;

const AltSignupText = styled(Text)`
  width: 100%;
  font-size: ${toRem(12)};
  color: ${theme.color.neutral[500]};
  text-align: center;
`;

const validationSchema = Yup.object().shape({
  identity: Yup.string()
    .test('IsIdentity', 'Please enter a valid email address.', (identity: string) => isEmail(identity || ''))
    .required('Please enter a valid email address.'),
  password: Yup.string().required('Please enter a valid password.'),
});

const formatError = (error: string) => {
  if (error.toLowerCase().trim() === 'failure logging in v1 user') {
    return 'Email and password combination incorrect';
  }
  return error;
};

const Login = ({ handleGoogleLogin, authHeader, onForgotPasswordClick, authTerms, authMarketing = <AuthMarketing />, signupText, renderMFAVerification }: ILoginProps) => {
  const dispatch = useDispatch();
  const integration = useConnectIntegration();
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.App.LOGIN_REQUEST));
  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, ActionTypes.App.LOGIN_REQUEST)) as IApiErrorPayloadWithCode;
  const [showMFAVerify, setShowMFAVerify] = useState<IMFARequired>({ required: false });

  const { errors, isValid, touched, handleSubmit, getFieldProps, values } = useFormik<IAuthRequest>({
    initialValues: { type: AuthType.KICKFIN, identity: '', password: '', deviceId: DeviceService.getDeviceId() },
    onSubmit: () => {
      dispatch(loginRequest(values, onMFARequired, undefined, integration?.name));
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema,
  });

  const validateField = (field: 'identity' | 'password') => Boolean(touched[field] && errors[field]);

  const onMFARequired = (mfa: IAuthMFARequired) => {
    dispatch(clearAllRequests());
    setShowMFAVerify({ required: true, mfa });
  };

  const onMFABack = () => {
    setShowMFAVerify({ required: false });
  };

  const renderLoginForm = () => (
    <>
      <AuthLogoutNotice />
      {authHeader}
      <AuthContentLayout>
        <AuthButtonGroup>
          <ButtonSignUp data-cy="google-login-button" icon="Google" label="Log in with Google" onClick={handleGoogleLogin} />
        </AuthButtonGroup>
        <AuthOrContainer>Or</AuthOrContainer>
        <Form onSubmit={handleSubmit}>
          <AuthFieldset>
            <FieldWrapper data-cy="identity-field" label="Email Address" message={validateField('identity') ? errors.identity : ''} hasError={validateField('identity')}>
              <Input type="email" disabled={isLoading} placeholder="you@email.com" {...getFieldProps('identity')} />
            </FieldWrapper>
            <FieldWrapper data-cy="password-field" label="Password" action="Forgot password?" onAction={onForgotPasswordClick} message={validateField('password') ? errors.password : ''} hasError={validateField('password')}>
              <Password disabled={isLoading} data-cy="password-input" id="password" {...getFieldProps('password')} />
            </FieldWrapper>
          </AuthFieldset>
          {apiErrors?.errorMessages &&
            apiErrors.errorMessages.map((error, index) => (
              <FormError data-cy="form-error" key={`error-message-${index}`}>
                {formatError(error)}
              </FormError>
            ))}
          <Button label="Log In" data-cy="submit-button" type="submit" appearance="primary" size="large" disabled={isLoading || !isValid} isProcessing={isLoading} />
        </Form>
      </AuthContentLayout>
      <AltSignupText>{signupText}</AltSignupText>
    </>
  );

  return (
    <LayoutSplit
      left={
        <AuthContainer>
          <AuthContent>
            <AltAuthContentInner>
              {!showMFAVerify.required && renderLoginForm()}
              {showMFAVerify.required && showMFAVerify.mfa && renderMFAVerification({ mfaRequired: showMFAVerify.mfa, onBack: () => onMFABack() })}
              {authTerms}
            </AltAuthContentInner>
          </AuthContent>
        </AuthContainer>
      }
      right={authMarketing}
    />
  );
};

export default Login;
